/* eslint-disable max-len */
import dayjs, { Dayjs } from "dayjs";
import * as User from "../../../providers/user";
import * as Axios from "providers/axios";
import RoleArtistCalendar from "./RoleArtistCalendar";
import { useLocaleState, useRefresh, useTranslate } from "react-admin";
import { useRef, useState } from "react";
import { IShowAppointmentServiceDialogData } from "components/ArtistsCalendarList";
import { GET_APPOINTMENT_SERVICES } from "gql";
import { useQuery } from "@apollo/client";
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotesIcon from "@mui/icons-material/Notes";
import DeleteIcon from "@mui/icons-material/Delete";
import AppointmentServiceEdit from "components/AppointmentServiceEdit";
import AppointmentServiceShow from "components/AppointmentServiceShow";
import { CustomButton } from "components/CustomButton";
import { muiColors } from "themes/colors";
import { Link } from "react-router-dom";
import ConfirmationModal from "components/ConfirmationModal";
import CancellationModal from "components/CancellationModal";

const DashboardRoleArtistCalendar = ({
  date,
  timezone
}: {
  date: Dayjs | null;
  timezone: string;
}) => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const { user, permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const [localeState] = useLocaleState();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeNote, setActiveNote] = useState<string>("");
  const [editDialogId, setEditDialogId] = useState<string>("");
  const [showDialogData, setShowDialogData] =
    useState<IShowAppointmentServiceDialogData | null>(null);
  const [blockTimeDeleteId, setBlockTimeDeleteId] = useState<
    string | undefined
  >("");
  const [cancelModalAppointmentId, setCancelModalAppointmentId] = useState<
    string | null
  >(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  const dateInTZ = date ? date.tz(timezone, true) : null;

  const nowInTZ = dayjs().tz(timezone, true);

  const isToday = dateInTZ ? dateInTZ.isSame(nowInTZ, "day") : false;

  const dateSeparatorDate = dateInTZ
    ? isToday
      ? `Today, ${dateInTZ.format("MMM D, YYYY")}`
      : dateInTZ.format("dddd, MMM D, YYYY")
    : "";

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  const appointmentsResult = useQuery(GET_APPOINTMENT_SERVICES, {
    variables: {
      where: {
        AND: {
          artist_id: {
            equals: user?.artist?.id
            // equals: "6d5d0d7d-560c-4b05-8521-24439492f72f"
          },
          start_time: {
            gte: date
              ? dayjs(date).startOf("day").subtract(1, "day").format()
              : dayjs().startOf("day").format(),
            lte: date
              ? dayjs(date).endOf("day").add(1, "day").format()
              : dayjs().endOf("day").format()
          },
          appointment: {
            is: {
              cancelled_at: {
                equals: null
              },
              location_id: {
                // in: locationFilter === "all" ? locationIds : [locationFilter]
                in: locationIds
              }
            }
          }
        }
      }
    },
    fetchPolicy: "no-cache",
    pollInterval: 15000
  });

  const appointmentData =
    appointmentsResult?.data ?? appointmentsResult?.previousData;

  // const allAppointmentServicesWithoutTimezone =
  //   appointmentData?.appointments?.reduce((acc: any, appointment: any) => {
  //     return [...acc, ...appointment.appointment_services];
  //   }, []);

  const handlePopoverOpen = (note: string) => {
    if (popoverRef.current) {
      setActiveNote(note);
      setAnchorEl(popoverRef.current);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActiveNote("");
  };

  const closeShowDialog = () => {
    setShowDialogData(null);
  };
  const closeEditDialog = () => {
    setEditDialogId("");
    refresh();
  };

  const deleteBlockTimeHandler = async (appointmentId: string) => {
    try {
      await api.delete(`/v1/artists/block-time/${appointmentId}`);
      setBlockTimeDeleteId("");
      setShowDialogData(null);
      appointmentsResult?.refetch();
    } catch (error) {
      setBlockTimeDeleteId("");
    }
  };

  if (!permissions?.companyId) return null;

  return (
    <div
      ref={popoverRef}
      onClick={() => !!activeNote && handlePopoverClose()}
      aria-hidden
      className="w-full h-full relative"
    >
      <RoleArtistCalendar
        date={date ?? dayjs()}
        locale={localeState}
        // select={handleSelectRange}
        editable={false}
        selectable={false}
        anchorEl={anchorEl}
        setShowDialogData={setShowDialogData}
        isPopoverOpen={open}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
        type={"appointmentService"}
        // artistId={
        //   artist?.id || `non-existing-artist-${index}`
        // }
        // isPrev={isPrev}
        // calendarsDate={calendarsDate}
        // setSelectedCalendarDate={setSelectedCalendarDate}
        // setCalendarsDate={setCalendarsDate}
        // isNext={isNext}
        // index={index}
        appointmentServicesData={appointmentData?.appointmentServices}
        // businessHours={businessHours}
      />
      {!!showDialogData && (
        <Dialog
          open={!!showDialogData}
          onClose={closeShowDialog}
          sx={{
            "& .MuiDialogContent-root": {
              padding: "0px"
            },
            "& .MuiDialog-paper": {
              borderRadius: "8px",
              backgroundColor: "#fff",
              maxWidth: "520px",
              width: "100%"
            }
          }}
        >
          <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
            <Button
              sx={{
                minWidth: "unset",
                whiteSpace: "nowrap",
                fontSize: "12px"
              }}
              startIcon={<NotesIcon />}
              onClick={() => {
                if (showDialogData?.appointmentId) {
                  setEditDialogId(showDialogData.appointmentId);
                }
              }}
            >
              {translate("appointment.note")}
            </Button>
            <div className="w-full flex uppercase justify-center items-center ml-[-12px] text-[14px] font-['Inter'] font-[600]">
              {showDialogData?.isBlockedTime
                ? `${translate("appointment.blocked_time")}`
                : "POINTMENT"}
            </div>
            <IconButton onClick={closeShowDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <AppointmentServiceShow
              showDialogData={showDialogData}
              setShowDialogData={setShowDialogData}
              refresh={() => appointmentsResult?.refetch()}
            />
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: showDialogData?.isBlockedTime
                ? "flex-end"
                : "space-between"
            }}
          >
            {showDialogData?.isBlockedTime ? (
              <CustomButton
                text={translate("common.delete")}
                variant="secondary"
                onClick={() =>
                  setBlockTimeDeleteId(showDialogData?.appointmentId)
                }
                sx={{
                  color: muiColors.error,
                  border: `solid 1px ${muiColors.error}`,
                  svg: {
                    fill: muiColors.error,
                    path: {
                      fill: muiColors.error
                    }
                  }
                }}
                startIcon={<DeleteIcon />}
              />
            ) : permissions?.isArtist ? (
              <div />
            ) : (
              <CustomButton
                text={translate("appointment.cancel_full_appointment")}
                variant="secondary"
                onClick={() =>
                  setCancelModalAppointmentId(
                    showDialogData?.appointmentId || null
                  )
                }
                sx={{
                  color: muiColors.error,
                  border: `solid 1px ${muiColors.error}`,
                  svg: {
                    fill: muiColors.error,
                    path: {
                      fill: muiColors.error
                    }
                  }
                }}
              />
            )}
            {!showDialogData?.isBlockedTime && (
              <Link
                to={`/dashboard/Appointment/${showDialogData?.appointmentId}/show`}
              >
                <CustomButton
                  variant="secondary"
                  text={translate("appointment.appointment_details")}
                />
              </Link>
            )}
          </DialogActions>
          {blockTimeDeleteId && (
            <ConfirmationModal
              open={!!blockTimeDeleteId}
              handleConfirm={() => deleteBlockTimeHandler(blockTimeDeleteId)}
              onClose={() => setBlockTimeDeleteId("")}
              text="Are you sure you want to delete? This action can't be reversed, so please be certain before proceeding."
              buttonText="Delete"
            />
          )}
          {!blockTimeDeleteId && cancelModalAppointmentId && (
            <CancellationModal
              cancelModalAppointmentId={cancelModalAppointmentId}
              setCancelModalAppointmentId={setCancelModalAppointmentId}
              refreshCalendarAndCloseShowModal={() => {
                appointmentsResult?.refetch();
                closeShowDialog();
              }}
            />
          )}
        </Dialog>
      )}
      {!!editDialogId && (
        <Dialog
          open={!!editDialogId}
          onClose={() => setEditDialogId("")}
          sx={{
            "& .MuiDialogContent-root": {
              padding: "0px"
            },
            "& .MuiDialog-paper": {
              borderRadius: "8px",
              backgroundColor: "#fff",
              maxWidth: "410px",
              width: "100%"
            }
          }}
        >
          <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
            <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600]">
              {translate("appointment.note")}
            </div>
            <IconButton onClick={() => setEditDialogId("")}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <AppointmentServiceEdit
              editDialogId={editDialogId}
              closeEditDialog={closeEditDialog}
              setShowDialogData={setShowDialogData}
            />
          </DialogContent>
        </Dialog>
      )}
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: "none"
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div className="bg-mono-white p-[70px] max-w-[500px] text-[22px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor">
          {activeNote}
        </div>
      </Popover>
      <div
        className="h-[50px] bg-[#FAF9F7] absolute top-[3px] left-[57px] right-[18px] flex justify-between items-center rounded-t-lg"
        style={{ borderBottom: "solid 1px #ddd" }}
      >
        <div className="flex items-center gap-1">
          <Avatar
            sx={{
              bgcolor: "rgba(238, 234, 228, 0.9)",
              color: "rgba(48, 47, 46, 0.95)",
              width: "32px",
              height: "32px",
              fontFamily: "Inter",
              fontSize: "18px",
              "@media screen and (max-width:800px)": {
                width: "24px",
                height: "24px"
              }
            }}
            alt={user?.first_name}
            src={user?.artist?.cover_photo_url || user?.photo_url || "monogram"}
          />
          <div className="text-[14px] leading-6 font-[400]">
            {user?.first_name} ({translate("dashboard.you")})
          </div>
        </div>
        <div className="text-[14px] leading-6 font-[500]">
          {dateSeparatorDate}
        </div>
      </div>
    </div>
  );
};

export default DashboardRoleArtistCalendar;
